import { useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from '@components/common/Button';
import { FormTextField } from '@components/common/FormTextField/FormTextField';
import { Select } from '@components/common/Select/Select';
import { ISelectOption } from '../../../components/common/Select/types';
import { addressToOptionTransformer } from '@pages/ApplicationFormPage/AddAddress/AddAddress';
import { addAddressQuestions } from '../../ApplicationFormPage/AddAddress/questions';
import { useAddressesByPostcode } from '@queries/useAddressByPostcode/useAddressesByPostcode';
import { mapPostcodeAddressToForm } from './mappers';
import { OrganisationAddressForm } from './types/address';

export interface AddressFieldPaths {
  addressLine1: string;
  addressLine2: string;
  postcode: string;
  town: string;
  county: string;
  country: string;
}

export type AddressFieldsProps = {
  fieldPaths: AddressFieldPaths;
  manualTogglePath: string;
  addressValuePath: string;
  disabled?: boolean;
};

const inputClass = { sx: { padding: '6px !important' } as const, className: 'text-sm font-normal!' as const };

export function AddressFields({
  fieldPaths,
  manualTogglePath,
  addressValuePath,
  disabled = false,
}: AddressFieldsProps) {
  const form = useFormContext<any>();

  const intl = useIntl();
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  const isManual = useWatch({
    name: manualTogglePath,
    control: form.control,
    defaultValue: false,
  }) as boolean;
  const postcode =
    (useWatch({
      name: fieldPaths.postcode,
      control: form.control,
      defaultValue: '',
    }) as string) || '';

  const { data: addresses = [], refetch: fetchAddressesByPostcode } = useAddressesByPostcode(postcode);
  const addressOptions = useMemo(() => addresses.map(addressToOptionTransformer), [addresses]);
  const addressBlock = useWatch({ name: addressValuePath, control: form.control }) as
    | OrganisationAddressForm
    | undefined;

  const [listSelection, setListSelection] = useState<string>('');

  const setGroupAddress = (body: OrganisationAddressForm) => {
    form.setValue(addressValuePath, body, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const setManualHandler = () => {
    form.setValue(manualTogglePath, true);
    setListSelection('');
  };

  const setHandlerByPostcode = () => {
    form.setValue(manualTogglePath, false);
  };

  useEffect(() => {
    if (addressOptions.length > 0 && !isManual && !addressBlock?.addressLine1) {
      const firstOptionValue = String(addressOptions[0].value);
      setListSelection(firstOptionValue);
      const matched = addresses.find(a => a.fullAddress === firstOptionValue);
      if (matched) {
        setGroupAddress(mapPostcodeAddressToForm(matched));
      }
    }
  }, [addressOptions, isManual, addressBlock?.addressLine1, addresses]);

  return (
    <ul className='list-none pl-0 col-span-1 md:col-span-2'>
      <li>
        <FormattedMessage id={addAddressQuestions.postcode.labelKey} />
        <div className='flex items-center justify-between gap-3'>
          <FormTextField
            name={fieldPaths.postcode}
            rules={disabled ? undefined : { required: { value: true, message: requiredFieldErrorMessage } }}
            disabled={disabled}
            textFieldProps={{
              type: 'text',
              inputProps: {
                'aria-label': intl.formatMessage({ id: addAddressQuestions.postcode.labelKey }),
                ...inputClass,
              },
            }}
          />
          {!isManual && (
            <div>
              <Button
                className='w-40 h-8'
                variant='outlined'
                disabled={!postcode.length || Boolean(disabled)}
                onClick={() => fetchAddressesByPostcode()}
              >
                <FormattedMessage id='applicationForm.ADD_ADDRESS.findAddress' />
              </Button>
            </div>
          )}
        </div>
      </li>
      {isManual ? (
        <>
          <li className='mt-2 w-full'>
            <FormattedMessage id={addAddressQuestions.address1.labelKey} />
            <FormTextField
              name={fieldPaths.addressLine1}
              rules={disabled ? undefined : { required: { value: true, message: requiredFieldErrorMessage } }}
              disabled={disabled}
              textFieldProps={{
                type: 'text',
                inputProps: {
                  'aria-label': intl.formatMessage({ id: addAddressQuestions.address1.labelKey }),
                  ...inputClass,
                },
              }}
            />
          </li>
          <li className='mt-2 w-full'>
            <FormattedMessage id={addAddressQuestions.address2.labelKey} />
            <FormTextField
              name={fieldPaths.addressLine2}
              disabled={disabled}
              textFieldProps={{
                type: 'text',
                inputProps: {
                  'aria-label': intl.formatMessage({ id: addAddressQuestions.address2.labelKey }),
                  ...inputClass,
                },
              }}
            />
          </li>
          <li className='mt-2 w-full'>
            <FormattedMessage id={addAddressQuestions.city.labelKey} />
            <FormTextField
              name={fieldPaths.town}
              rules={disabled ? undefined : { required: { value: true, message: requiredFieldErrorMessage } }}
              disabled={disabled}
              textFieldProps={{
                type: 'text',
                inputProps: {
                  'aria-label': intl.formatMessage({ id: addAddressQuestions.city.labelKey }),
                  ...inputClass,
                },
              }}
            />
          </li>
          <li className='mt-2 w-full'>
            <span className='mb-1 block text-sm'>
              <FormattedMessage id='applicationForm.ADD_ADDRESS.county.label' />
            </span>
            <FormTextField
              name={fieldPaths.county}
              disabled={disabled}
              textFieldProps={{
                type: 'text',
                inputProps: { ...inputClass },
              }}
            />
          </li>
          <li className='mt-2 w-full'>
            <FormattedMessage id={addAddressQuestions.country.labelKey} />
            <FormTextField
              name={fieldPaths.country}
              rules={disabled ? undefined : { required: { value: true, message: requiredFieldErrorMessage } }}
              disabled={disabled}
              textFieldProps={{
                type: 'text',
                inputProps: {
                  'aria-label': intl.formatMessage({ id: addAddressQuestions.country.labelKey }),
                  ...inputClass,
                },
              }}
            />
          </li>
          {!disabled && (
            <p className='cursor-pointer font-semibold text-sky-500 underline' onClick={setHandlerByPostcode}>
              <FormattedMessage id='applicationForm.ADD_ADDRESS.wantToFindAddress' />
            </p>
          )}
        </>
      ) : (
        <>
          <div>
            <FormattedMessage id={addAddressQuestions.fullAddress.labelKey} />
            <div className='mt-1'>
              <Select
                disableClearable
                className='w-full'
                options={addressOptions}
                filterOptions={(options: ISelectOption[]) => options}
                value={listSelection || addressOptions[0]?.value || null}
                onChange={(_event: React.ChangeEvent<HTMLSelectElement>, option: ISelectOption | null) => {
                  if (!option) {
                    return;
                  }
                  setListSelection(String(option.value));
                  const found = addresses.find(a => a.fullAddress === String(option.value));
                  if (found) {
                    setGroupAddress(mapPostcodeAddressToForm(found));
                  }
                }}
                disabled={disabled}
                placeholder={intl.formatMessage({ id: 'placeholders.select' })}
              />
            </div>
          </div>
          {!disabled && (
            <p className='mt-2 cursor-pointer font-semibold text-sky-500 underline' onClick={setManualHandler}>
              <FormattedMessage id='applicationForm.ADD_ADDRESS.cantFindAddress' />
            </p>
          )}
        </>
      )}
    </ul>
  );
}
